// Shared constants & helpers for admin components
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const LOGO_URL = "https://customer-assets.emergentagent.com/job_60566f20-32da-458f-94ff-7bee79b2ab60/artifacts/dzka9ng8_Munch%27d%20Logo.png";
export const COLOR_OPTIONS = ["#1aa59a", "#E74C3C", "#27AE60", "#3498DB", "#9B59B6", "#F39C12", "#1ABC9C", "#E91E63"];

// Shared cache for admin item images (lazy-loaded)
export const adminImageCache = new Map();

/**
 * Invalidate every cached copy of a menu-item image and notify any mounted
 * `<AdminItemImage>` / customer `MenuCard` instances so they re-fetch.
 *
 * Call this after a successful create/update of a menu item to make sure the
 * newly-saved picture appears immediately (fixes the "old image still shows
 * after saving" bug).
 */
export const invalidateMenuItemImage = (itemId) => {
  if (!itemId) return;
  adminImageCache.delete(itemId);
  try {
    // Best-effort — dynamic import avoids a circular dep with MenuCard.jsx
    // We just fire a window event; both image components listen for it.
    window.dispatchEvent(
      new CustomEvent("menu-image-updated", { detail: { itemId } })
    );
  } catch {
    /* no-op */
  }
};

// Read an image File as base64 data URL with size guard
import { toast } from "sonner";
import axios from "axios";

// ---- Admin session (token kept in this browser tab only) ----
const TOKEN_KEY = "munchd_admin_token";
export const getAdminToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setAdminToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearAdminToken = () => sessionStorage.removeItem(TOKEN_KEY);
// Must match the 401 details raised by backend services/admin_auth.require_admin
const ADMIN_AUTH_ERRORS = [
  "Admin login required",
  "Session expired - please log in again",
  "Invalid admin session",
];

axios.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token && config.url?.startsWith(API)) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
  return config;
});

axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error.config?.url || "";
    const path = url.startsWith(API) ? url.slice(API.length) : "";
    const detail = error.response?.data?.detail;
    const isAdminCall =
      ADMIN_AUTH_ERRORS.includes(detail) || (path.startsWith("/admin/") && path !== "/admin/verify");
    if (error.response?.status === 401 && isAdminCall) {
      clearAdminToken();
      window.dispatchEvent(new Event("admin-session-expired"));
    }
    return Promise.reject(error);
  }
);

export const handleImageUpload = (e, setter) => {
  const file = e.target.files[0];
  if (!file) return;

  if (file.size > 5 * 1024 * 1024) {
    toast.error("Image must be smaller than 5MB");
    return;
  }

  const reader = new FileReader();
  reader.onload = (event) => setter(event.target.result);
  reader.readAsDataURL(file);
};
